<script setup>
// Matrix legend ("Key") popover — explains the fixture-matrix cell vocabulary
// (won / lost / drawn / pending approval / points, plus the schedule states).
// Shared by the league-cycle view and the tournament group-stage view, both of
// which render FixtureMatrixCell. Drop it inside a `.tl-action-strip` so the
// trigger button picks up the global `.as` styling.
const {t} = useI18n();
</script>

<template>
  <v-menu location="bottom" :close-on-content-click="true">
    <template #activator="{ props: menuProps }">
      <button class="as" v-bind="menuProps">
        <v-icon size="17" icon="mdi-map-legend"/>
        Key
      </button>
    </template>
    <div class="key-pop pa-4">
      <h4 class="mb-2 key-title">Key</h4>
      <div class="key-row">
        <div class="key-sample"><span class="sc sc--won">3–1</span></div>
        <div>Won. The score is to the player in the left column.</div>
      </div>
      <div class="key-row">
        <div class="key-sample"><span class="sc">1–3</span></div>
        <div>Lost.</div>
      </div>
      <div class="key-row">
        <div class="key-sample"><span class="sc sc--drawn">2–2</span></div>
        <div>Drawn.</div>
      </div>
      <div class="key-row">
        <div class="key-sample"><span class="sc-wrap"><span class="sc">3–1</span><span class="pend"></span></span></div>
        <div>{{ t('fixture_key_approval_waiting') }}</div>
      </div>
      <div class="key-row">
        <div class="key-sample"><span class="sc"><span class="pts">1</span>3–1</span></div>
        <div>{{ t('fixture_key_approved') }}</div>
      </div>
      <h4 class="mb-2 mt-4 key-title">Schedule key</h4>
      <div class="key-row">
        <div class="key-sample"><span class="dt">11/Feb<br>13:00</span></div>
        <div>Confirmed schedule. Both players have agreed.</div>
      </div>
      <div class="key-row">
        <div class="key-sample"><span class="dt dt--tbc">11/Feb<br>13:00</span></div>
        <div>Proposed schedule, waiting for confirmation.</div>
      </div>
      <div class="key-row">
        <div class="key-sample"><span class="add">+</span></div>
        <div>Enter a result or schedule the match.</div>
      </div>
      <div class="key-row">
        <div class="key-sample"><span class="dash">–</span></div>
        <div>Not played yet.</div>
      </div>
    </div>
  </v-menu>
</template>

<style scoped lang="scss">
/* Cell vocabulary samples — mirror FixtureMatrixCell so the key matches the grid. */
.sc-wrap {
  position: relative;
  display: inline-block;
}

.sc {
  position: relative;
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 7px;
  border-radius: 7px;
  background: rgb(var(--v-theme-light));
  color: rgb(var(--v-theme-charcoal));
  white-space: nowrap;

  &.sc--won {
    background: rgb(var(--v-theme-successTint));
    color: rgb(var(--v-theme-success));
  }

  &.sc--drawn {
    background: rgb(var(--v-theme-light));
    color: rgb(var(--v-theme-text));
  }

  .pts {
    font-size: 9px;
    font-weight: 700;
    opacity: 0.65;
    margin-right: 4px;
    padding-right: 4px;
    border-right: 1px solid currentColor;
  }
}

.pend {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgb(var(--v-theme-warning));
  border: 1.5px solid rgb(var(--v-theme-surface));
}

.dt {
  display: inline-block;
  font-size: 9.5px;
  font-weight: 600;
  line-height: 1.25;
  color: rgb(var(--v-theme-charcoal));
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-border));
  border-radius: 7px;
  padding: 3px 6px;
  white-space: nowrap;

  &.dt--tbc {
    border-style: dashed;
    color: rgb(var(--v-theme-warning));
  }
}

.add {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgb(var(--v-theme-surface));
  border: 1.5px dashed rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-primary));
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
}

.dash {
  color: #D8D8D8;
  font-size: 13px;
  font-weight: 600;
}

/* Popup shell */
.key-pop {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  max-width: 340px;
}

.key-title {
  font-size: 12px;
  font-weight: 700;
}

.key-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 0;
  font-size: 11.5px;
  color: rgb(var(--v-theme-charcoal));

  & + .key-row {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }

  .key-sample {
    width: 64px;
    flex-shrink: 0;
    text-align: center;
  }
}
</style>
